import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import Toast from "react-bootstrap/Toast";
import ToastContainer from "react-bootstrap/ToastContainer";
import {
  LuTriangleAlert,
  LuFileSearch,
  LuRotateCcw,
  LuShieldAlert,
  LuFileText,
} from "react-icons/lu";

import useSubmission from "../Submissions/useSubmission";
import CertifierForm from "../Submissions/CertificationForm";
import CertifiedView from "../Submissions/CertifiedView";

type SubmissionTabProps = {
  leaName: string;
  vendorNumber: string;
  certificationCycle: string;
  certificationStep: string;
  certificationStatus: string;
  certifierFirstName?: string;
  certifierLastName?: string;
  certifiedDate?: string;
  isCycleActive: boolean;
  isCycleEnded?: boolean;
  canCertify: boolean;
  reportsReviewed?: number | null;
  totalReports?: number;
  onCertify?: (data: {
    firstName: string;
    lastName: string;
    email: string;
    phoneNumber: string;
    extension: string;
  }) => Promise<boolean> | boolean | void;
  onDecertify?: () => void;
  isSubmitting?: boolean;
};

function BlockingState({
  iconBg,
  iconColor,
  icon: Icon,
  title,
  description,
  extra,
}: {
  iconBg: string;
  iconColor: string;
  icon: React.ElementType;
  title: string;
  description: React.ReactNode;
  extra?: React.ReactNode;
}) {
  return (
    <div
      className="d-flex flex-column align-items-center justify-content-center text-center"
      style={{ padding: "4rem 2rem" }}
    >
      <div
        className="d-flex align-items-center justify-content-center rounded-circle mb-4"
        style={{
          width: 72,
          height: 72,
          backgroundColor: iconBg,
          border: `1px solid ${iconBg}`,
        }}
      >
        <Icon size={32} style={{ color: iconColor }} aria-hidden="true" />
      </div>
      <h5
        className="fw-bold mb-2"
        style={{ fontSize: "1.25rem", color: "#0f172a" }}
      >
        {title}
      </h5>
      <p
        className="mb-0"
        style={{
          color: "#64748b",
          fontSize: "0.9375rem",
          maxWidth: 400,
          lineHeight: 1.6,
        }}
      >
        {description}
      </p>
      {extra && <div className="mt-3">{extra}</div>}
    </div>
  );
}

export default function SubmissionTab({
  leaName,
  vendorNumber,
  certificationCycle,
  certificationStep,
  certificationStatus,
  certifierFirstName,
  certifierLastName,
  certifiedDate,
  isCycleActive,
  isCycleEnded = false,
  canCertify,
  reportsReviewed = null,
  totalReports,
  onCertify,
  onDecertify,
  isSubmitting = false,
}: SubmissionTabProps) {
  const {
    isCertified,
    displayStep,
    certifierName,
    displayCertifiedDate,
    showCertifyToast,
    showDecertifyToast,
    dismissCertifyToast,
    dismissDecertifyToast,
    showDecertifyDialog,
    openDecertifyDialog,
    closeDecertifyDialog,
    handleCertify,
    confirmDecertify,
  } = useSubmission({
    certificationStatus,
    certificationStep,
    certifierFirstName,
    certifierLastName,
    certifiedDate,
    onCertify,
    onDecertify,
  });

  const dialogRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!showDecertifyDialog) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        closeDecertifyDialog();
        return;
      }
      if (e.key === "Tab" && dialogRef.current) {
        const focusableElements = dialogRef.current.querySelectorAll(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        const firstElement = focusableElements[0] as HTMLElement;
        const lastElement = focusableElements[
          focusableElements.length - 1
        ] as HTMLElement;
        if (e.shiftKey && document.activeElement === firstElement) {
          e.preventDefault();
          lastElement?.focus();
        } else if (!e.shiftKey && document.activeElement === lastElement) {
          e.preventDefault();
          firstElement?.focus();
        }
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    const firstFocusable = dialogRef.current?.querySelector<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    firstFocusable?.focus();
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [showDecertifyDialog, closeDecertifyDialog]);

  const toastPortal =
    showCertifyToast || showDecertifyToast
      ? createPortal(
          <ToastContainer
            position="top-end"
            className="p-3"
            style={{ zIndex: 2000 }}
          >
            {showCertifyToast && (
              <Toast
                show
                onClose={dismissCertifyToast}
                delay={6000}
                autohide
                className="toastSuccessCard"
              >
                <div className="toastSuccessInner">
                  <div className="toastSuccessStrip" />
                  <div className="toastSuccessMain">
                    <div className="toastSuccessIcon">
                      <i className="bi bi-check-circle-fill" />
                    </div>
                    <div className="toastSuccessText">
                      <div className="toastSuccessTitle">
                        Submission Successful
                      </div>
                      <div className="toastSuccessMessage">
                        Data has been certified successfully.
                      </div>
                    </div>
                    <button
                      type="button"
                      className="btn-close ms-auto"
                      onClick={dismissCertifyToast}
                      aria-label="Close"
                      style={{ fontSize: "0.65rem" }}
                    />
                  </div>
                </div>
              </Toast>
            )}
            {showDecertifyToast && (
              <Toast
                show
                onClose={dismissDecertifyToast}
                delay={6000}
                autohide
                className="toastSuccessCard"
              >
                <div className="toastSuccessInner">
                  <div className="toastSuccessStrip" />
                  <div className="toastSuccessMain">
                    <div className="toastSuccessIcon">
                      <i className="bi bi-check-circle-fill" />
                    </div>
                    <div className="toastSuccessText">
                      <div className="toastSuccessTitle">Decertified</div>
                      <div className="toastSuccessMessage">
                        Certification has been removed successfully.
                      </div>
                    </div>
                    <button
                      type="button"
                      className="btn-close ms-auto"
                      onClick={dismissDecertifyToast}
                      aria-label="Close"
                      style={{ fontSize: "0.65rem" }}
                    />
                  </div>
                </div>
              </Toast>
            )}
          </ToastContainer>,
          document.body
        )
      : null;

  const decertifyDialog = showDecertifyDialog
    ? createPortal(
        <div
          className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
          style={{ backgroundColor: "rgba(0, 0, 0, 0.6)", zIndex: 1050 }}
          onClick={closeDecertifyDialog}
        >
          <div
            ref={dialogRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby="decertify-title"
            aria-describedby="decertify-message"
            className="rounded-4 shadow-lg"
            style={{
              backgroundColor: "#fff",
              width: "100%",
              maxWidth: "520px",
              overflow: "hidden",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-4 pt-4 pb-4">
              <h5
                id="decertify-title"
                className="fw-bold mb-3"
                style={{ fontSize: "1.25rem" }}
              >
                Decertify
              </h5>
              <p
                id="decertify-message"
                className="mb-0"
                style={{
                  fontSize: "0.95rem",
                  lineHeight: 1.6,
                  color: "#374151",
                }}
              >
                You have already certified your data for this certification
                cycle. If you decertify now to make edits, you will need to
                resolve any fatal errors and recertify your revised data
                before the end of the certification window. Are you sure you
                want to continue?
              </p>
            </div>
            <div
              className="d-flex justify-content-end gap-3 px-4 py-3"
              style={{
                backgroundColor: "#f9fafb",
                borderTop: "1px solid #f1f5f9",
              }}
            >
              <button
                type="button"
                className="btn"
                onClick={closeDecertifyDialog}
                style={{
                  backgroundColor: "#ffffff",
                  border: "1px solid #d1d5db",
                  color: "#374151",
                  fontSize: "0.9rem",
                  fontWeight: 500,
                  padding: "0.55rem 1.5rem",
                  borderRadius: "0.5rem",
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn d-flex align-items-center justify-content-center gap-2"
                onClick={confirmDecertify}
                style={{
                  backgroundColor: "#b45309",
                  border: "1px solid #b45309",
                  color: "#ffffff",
                  fontSize: "0.9rem",
                  fontWeight: 600,
                  padding: "0.55rem 1.5rem",
                  borderRadius: "0.5rem",
                }}
              >
                <LuRotateCcw size={15} />
                Decertify
              </button>
            </div>
          </div>
        </div>,
        document.body
      )
    : null;

  if (!canCertify) {
    if (isCertified) {
      return (
        <>
          <CertifiedView
            leaName={leaName}
            vendorNumber={vendorNumber}
            certificationCycle={certificationCycle}
            certifierName={certifierName}
            certifiedDate={displayCertifiedDate}
            showDecertify={false}
          />
          {toastPortal}
        </>
      );
    }

    return (
      <>
        <BlockingState
          iconBg="#f1f5f9"
          iconColor="#475569"
          icon={LuShieldAlert}
          title="Permission Required"
          description="You do not have permission to certify or decertify this cycle. Contact your administrator if you believe this is an error."
        />
        {toastPortal}
      </>
    );
  }

  if (isCycleEnded) {
    if (isCertified || certificationStatus === "Certified") {
      return (
        <>
          <CertifiedView
            leaName={leaName}
            vendorNumber={vendorNumber}
            certificationCycle={certificationCycle}
            certifierName={certifierName}
            certifiedDate={displayCertifiedDate}
            showDecertify={false}
          />
          {toastPortal}
        </>
      );
    }
    return (
      <>
        <BlockingState
          iconBg="#f1f5f9"
          iconColor="#64748b"
          icon={LuFileText}
          title="Certification Deadline Passed"
          description="The amendment deadline for this certification cycle has passed. No further changes can be made."
        />
        {toastPortal}
      </>
    );
  }

  if (!isCycleActive) {
    if (certificationStatus === "Certified") {
      return (
        <>
          <CertifiedView
            leaName={leaName}
            vendorNumber={vendorNumber}
            certificationCycle={certificationCycle}
            certifierName={certifierName}
            certifiedDate={displayCertifiedDate}
            showDecertify={false}
          />
          {toastPortal}
        </>
      );
    }
    return (
      <>
        <BlockingState
          iconBg="#f1f5f9"
          iconColor="#64748b"
          icon={LuFileText}
          title="Certification Window Closed"
          description="The data from this certification cycle did not get submitted during the certification window."
        />
        {toastPortal}
      </>
    );
  }

  if (displayStep === "Resolve Fatal Errors") {
    return (
      <>
        <BlockingState
          iconBg="#fef2f2"
          iconColor="#dc2626"
          icon={LuTriangleAlert}
          title="Resolve Fatal Errors"
          description="All fatal errors must be resolved before the data can be certified. Please visit the Validations tab to review and resolve each error."
        />
        {toastPortal}
      </>
    );
  }

  if (displayStep === "Verify Reports") {
    return (
      <>
        <BlockingState
          iconBg="#fefce8"
          iconColor="#b45309"
          icon={LuFileSearch}
          title="Review All Reports"
          description={
            <>
              All certification reports must be reviewed before the data can be
              certified. Please visit the <strong>Reports</strong> tab to review
              each report.
            </>
          }
          extra={
            <span
              style={{
                fontSize: "0.9375rem",
                fontWeight: 600,
                color: "#64748b",
              }}
            >
              {reportsReviewed === null || totalReports === undefined
                ? "Loading..."
                : `${reportsReviewed} / ${totalReports} reports reviewed`}
            </span>
          }
        />
        {toastPortal}
      </>
    );
  }

  if (isCertified) {
    return (
      <>
        <CertifiedView
          leaName={leaName}
          vendorNumber={vendorNumber}
          certificationCycle={certificationCycle}
          certifierName={certifierName}
          certifiedDate={displayCertifiedDate}
          showDecertify={isCycleActive}
          onDecertify={openDecertifyDialog}
        />
        {toastPortal}
        {decertifyDialog}
      </>
    );
  }

  return (
    <>
      <CertifierForm
        leaName={leaName}
        vendorNumber={vendorNumber}
        certificationCycle={certificationCycle}
        isSubmitting={isSubmitting}
        isCycleActive={isCycleActive}
        onSubmit={handleCertify}
      />
      {toastPortal}
      {decertifyDialog}
    </>
  );
}
